import {
  DataGridPro,
  GridColDef,
  GridDensity,
  GridEventListener,
  GridFilterModel,
  GridInitialState,
  GridProSlotsComponent,
  GridRowHeightParams,
  GridRowHeightReturnValue,
  GridRowIdGetter,
  GridRowId,
  GridSlotProps,
  GridRowClassNameParams,
  GridRowParams,
  GridRowSelectionModel,
  GridCallbackDetails,
  GridPaginationModel,
  GridFeatureMode,
  GridApiPro,
  GridSortModel,
  GridLocaleText,
} from '@mui/x-data-grid-pro';
import { Dispatch, RefObject, SetStateAction, useMemo } from 'react';
import { FieldValues } from 'react-hook-form';
import useGridColumns from './hooks/use-grid-columns-latest';
import { GridStateKey } from '../../types';
import {
  getSharedDataGridFilterTheme,
  getSharedDataGridTheme,
} from './shared-datagrid-theme';
import { useUserSettings } from '../utils/use-user-settings';
import { isFeatureEnabled } from '../utils/feature-flags';
import CustomFilterPanel from './datagrids/custom-filter-panel';
import CustomColumnsPanel from './datagrids/custom-columns-panel';
import { FilterPanelDeleteIcon } from './datagrids/shared-datagrids-utils';

import { useTheme, type SxProps, type Theme } from '@mui/material/styles';

export type DefaultGridState = Partial<{
  sorting: { sortModel: { field: string; sort: string }[] };
  columns: { columnVisibilityModel: Record<string, boolean> };
  filter: { filterModel: GridFilterModel };
  pagination: { paginationModel: GridPaginationModel };
}>;
interface SharedDataGridClientProps<
  Model extends FieldValues,
  SelectedRowsType extends GridRowId,
> {
  rows: Model[];
  columns: GridColDef[];
  gridStateKey?: GridStateKey;
  loading?: boolean;
  defaultGridState?: DefaultGridState;
  slots?: Partial<GridProSlotsComponent>;
  slotProps?: Partial<GridSlotProps>;
  selectedRows?: GridRowId[];
  setSelectedRows?: Dispatch<SetStateAction<SelectedRowsType[]>>;
  onRowClick?: GridEventListener<'rowClick'>;
  checkboxSelection?: boolean;
  disableRowSelectionOnClick?: boolean;
  disableColumnSelector?: boolean;
  disableColumnMenu?: boolean;
  gridKey?: string | null;
  showToolbar?: boolean;
  density?: GridDensity;
  onRowSelectionModelChange?: (
    rowSelectionModel: GridRowSelectionModel,
    details: GridCallbackDetails
  ) => void;
  onPaginationModelChange?: (
    model: GridPaginationModel,
    details: GridCallbackDetails<'pagination'>
  ) => void;
  getRowId?: GridRowIdGetter<Model>;
  sx?: SxProps<Theme>;
  getRowHeight?: (params: GridRowHeightParams) => GridRowHeightReturnValue;
  getRowClassName?: (params: GridRowClassNameParams<Model>) => string;
  keepNonExistentRowsSelected?: boolean;
  rowHeight?: number;
  hideFooterRowCount?: boolean;
  isRowSelectable?: (params: GridRowParams<Model>) => boolean;
  toolbarQuickFilterWidth?: number | string;
  pageSizeOptions?:
    | number[]
    | {
        value: number;
        label: string;
      }[];
  pagination?: boolean;
  rowCount?: number;
  paginationMode?: GridFeatureMode;
  paginationModel?: GridPaginationModel;
  apiRef?: RefObject<GridApiPro | null>;
  filterMode?: GridFeatureMode;
  filterModel?: GridFilterModel;
  onFilterModelChange?: (
    model: GridFilterModel,
    details: GridCallbackDetails<'filter'>
  ) => void;
  sortingMode?: GridFeatureMode;
  onSortModelChange?: (
    model: GridSortModel,
    details: GridCallbackDetails
  ) => void;
  onRowsScrollEnd?: GridEventListener<'rowsScrollEnd'>;
  localeText?: Partial<GridLocaleText>;
}

export default function SharedDataGridClient<
  Model extends FieldValues,
  SelectedRowsType extends GridRowId = GridRowId,
>({
  rows,
  getRowId,
  columns,
  gridStateKey,
  loading,
  slots,
  slotProps,
  selectedRows,
  setSelectedRows,
  onRowClick,
  checkboxSelection = false,
  disableRowSelectionOnClick = false,
  disableColumnSelector = false,
  disableColumnMenu = false,
  gridKey,
  defaultGridState,
  showToolbar = true,
  density,
  onPaginationModelChange,
  sx,
  getRowHeight,
  getRowClassName,
  keepNonExistentRowsSelected = false,
  rowHeight,
  hideFooterRowCount = false,
  isRowSelectable,
  onRowSelectionModelChange,
  toolbarQuickFilterWidth = 300,
  pageSizeOptions,
  pagination = false,
  rowCount,
  paginationMode = 'client',
  paginationModel,
  apiRef,
  filterMode = 'client',
  filterModel,
  onFilterModelChange,
  sortingMode = 'client',
  onSortModelChange,
  onRowsScrollEnd,
  localeText,
}: SharedDataGridClientProps<Model, SelectedRowsType>) {
  const theme = useTheme();
  const { features } = useUserSettings();
  const rowSelectionModel = useMemo(
    () => ({
      type: 'include' as const,
      ids: new Set(selectedRows ?? []),
    }),
    [selectedRows]
  );

  const defaultState: GridInitialState = {
    ...(defaultGridState?.sorting && {
      sorting: {
        sortModel: defaultGridState.sorting.sortModel.map((s) => ({
          field: s.field,
          sort: s.sort === 'asc' ? 'asc' : 'desc',
        })),
      },
    }),
    ...(defaultGridState?.columns && { columns: defaultGridState.columns }),
    ...(defaultGridState?.filter && { filter: defaultGridState.filter }),
    ...(defaultGridState?.pagination && {
      pagination: defaultGridState.pagination,
    }),
  };

  const { initialState, computedColumns } = useGridColumns(
    columns,
    gridStateKey
  );

  const defaultOnRowSelectionModelChange = (
    newModel: GridRowSelectionModel
  ) => {
    if (!setSelectedRows) return;
    const ids = Array.from(newModel.ids) as SelectedRowsType[];
    setSelectedRows(ids);
  };

  const uiEnhancementPhase2FeatureEnabled = isFeatureEnabled(
    'uiEnhancementPhase2',
    features
  );

  const panelSlots = useMemo(
    () =>
      uiEnhancementPhase2FeatureEnabled
        ? {
            filterPanel: CustomFilterPanel,
            filterPanelDeleteIcon: FilterPanelDeleteIcon,
            columnsPanel: CustomColumnsPanel,
          }
        : undefined,
    [uiEnhancementPhase2FeatureEnabled]
  );

  const mergedSlots = useMemo(
    () => ({ ...slots, ...panelSlots }),
    [slots, panelSlots]
  );

  const filterPanelInputVariant = uiEnhancementPhase2FeatureEnabled
    ? 'outlined'
    : 'standard';

  return (
    <DataGridPro
      apiRef={apiRef}
      showToolbar={showToolbar}
      key={gridKey}
      rows={rows}
      getRowId={getRowId}
      columns={gridStateKey ? computedColumns : columns}
      loading={loading}
      density={density ?? 'standard'}
      checkboxSelection={checkboxSelection}
      disableRowSelectionOnClick={disableRowSelectionOnClick}
      disableColumnSelector={disableColumnSelector}
      disableColumnMenu={disableColumnMenu}
      disableRowSelectionExcludeModel
      initialState={gridStateKey ? initialState || defaultState : defaultState}
      rowSelectionModel={rowSelectionModel}
      keepNonExistentRowsSelected={keepNonExistentRowsSelected}
      columnHeaderHeight={60}
      rowHeight={rowHeight || 60}
      hideFooterRowCount={hideFooterRowCount}
      onRowSelectionModelChange={
        onRowSelectionModelChange
          ? onRowSelectionModelChange
          : defaultOnRowSelectionModelChange
      }
      onPaginationModelChange={onPaginationModelChange}
      pageSizeOptions={pageSizeOptions}
      getRowClassName={getRowClassName}
      onRowClick={onRowClick}
      slots={mergedSlots}
      slotProps={{
        ...slotProps,
        columnsPanel: {
          sx: {
            '& .MuiDataGrid-columnsManagementHeader': {
              borderBottom: 'unset',
            },
            '& .MuiFormControlLabel-label': {
              fontSize: '1rem !important',
            },
          },
        },
        filterPanel: {
          filterFormProps: {
            logicOperatorInputProps: {
              variant: filterPanelInputVariant,
              size: 'small',
            },
            columnInputProps: {
              variant: filterPanelInputVariant,
              size: 'small',
            },
            operatorInputProps: {
              variant: filterPanelInputVariant,
              size: 'small',
            },
            valueInputProps: {
              InputComponentProps: {
                variant: filterPanelInputVariant,
              },
            },
          },
          sx: {
            ...getSharedDataGridFilterTheme(),
          },
        },
      }}
      getRowHeight={getRowHeight}
      isRowSelectable={isRowSelectable}
      pagination={pagination}
      rowCount={rowCount}
      paginationMode={paginationMode}
      paginationModel={paginationModel}
      filterMode={filterMode}
      filterModel={filterModel}
      onFilterModelChange={onFilterModelChange}
      sortingMode={sortingMode}
      onSortModelChange={onSortModelChange}
      onRowsScrollEnd={onRowsScrollEnd}
      localeText={{
        ...localeText,
        filterPanelRemoveAll: uiEnhancementPhase2FeatureEnabled
          ? 'Reset'
          : 'Remove All',
        filterPanelAddFilter: 'Add Filter',
      }}
      sx={{
        ...getSharedDataGridTheme(theme, {
          toolbarQuickFilterWidth,
          sx,
        }),
      }}
    />
  );
}
